import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { isValidNumber } from '../../../../../../../utils/math/number';
import { publish, SubscriptionTopic } from '../../../../../../../viewer/messaging/pubsub';
import { DesignTool, PlaceMode } from '../../../../../../../viewer/tools/design-tool';
import {
  openCreatePointByCoordinateDialog,
  openCreatePointByOffsetDialog,
} from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/point/create-from-dialog';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import { TextBox } from '../../../../../text-box/text-box';
import { CollapsableSection } from '../../../components/collapsable-section';
import { OptionLabels } from '../../../components/option-labels';

type Props = {
  expanded: boolean;
  tool: DesignTool;
};

export const PositionSection = (props: Props) => {
  const { t } = useTranslation();
  const [placeMode, setPlaceMode] = React.useState(props.tool.placeMode);
  const [length, setLength] = React.useState(props.tool.snapValues.length.toString());

  const onPlaceModeChanged = (newValue: PlaceMode) => {
    setPlaceMode(newValue);
    props.tool.onPlaceModeChanged(newValue);
  };

  const onLengthChanged = (newValue: string) => {
    setLength(newValue);

    const value = parseFloat(newValue);

    if (isValidNumber(value)) {
      props.tool.onSnapValuesChanged({ length: value });
    }
  };

  return (
    <CollapsableSection
      expanded={props.expanded}
      title="POSITION"
      onSetExpanded={(state) =>
        publish(SubscriptionTopic.DesignToolMenuStateChanged, { placeMode: state })
      }
    >
      <OptionLabels
        // Cut/fill towards the point cloud and the objects are the same, see placeMode
        active={
          placeMode === PlaceMode.IntersectPointcloud ? PlaceMode.IntersectObjects : placeMode
        }
        itemRows={[
          [
            {
              title: t('section.position.freeHand.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.Free,
              tooltipText: (
                <Trans
                  components={{ bold: <strong /> }}
                  i18nKey="section.position.freeHand.tooltip"
                  ns="skyviewDesign"
                />
              ),
              onSelect: () => onPlaceModeChanged(PlaceMode.Free),
            },
            {
              title: t('section.position.length.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.Length,
              tooltipText: (
                <Trans
                  components={{ bold: <strong /> }}
                  i18nKey="section.position.length.tooltip"
                  ns="skyviewDesign"
                />
              ),
              onSelect: () => onPlaceModeChanged(PlaceMode.Length),
              content: (
                <LabelledContainer
                  key={PlaceMode.Length}
                  text={t('section.position.length.inputTitle', { ns: 'skyviewDesign' })}
                >
                  {(formElementId) => (
                    <TextBox
                      autoFocus={true}
                      defaultValue={length}
                      id={formElementId()}
                      step={0.01}
                      type="number"
                      onChange={(e) => onLengthChanged(e.target.value)}
                    />
                  )}
                </LabelledContainer>
              ),
            },
          ],
          [
            {
              title: t('section.position.freePerpendicular.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.FreePerpendicular,
              tooltipText: t('section.position.freePerpendicular.tooltip', { ns: 'skyviewDesign' }),
              onSelect: () => onPlaceModeChanged(PlaceMode.FreePerpendicular),
            },
            {
              title: t('section.position.lengthPerpendicular.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.LengthPerpendicular,
              tooltipText: t('section.position.lengthPerpendicular.tooltip', {
                ns: 'skyviewDesign',
              }),
              onSelect: () => onPlaceModeChanged(PlaceMode.LengthPerpendicular),
              content: (
                <LabelledContainer
                  key={PlaceMode.LengthPerpendicular}
                  text={t('section.position.lengthPerpendicular.inputTitle', {
                    ns: 'skyviewDesign',
                  })}
                >
                  {(formElementId) => (
                    <TextBox
                      defaultValue={length}
                      id={formElementId()}
                      step={0.01}
                      type="number"
                      onChange={(e) => onLengthChanged(e.target.value)}
                    />
                  )}
                </LabelledContainer>
              ),
            },
          ],
          [
            {
              title: t('section.position.coordinate.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.Position,
              tooltipText: t('section.functions.createPoints.tooltip', { ns: 'skyviewDesign' }),
              // Not a place mode, but the dialogs of Create points
              onSelect: () => openCreatePointByCoordinateDialog(props.tool).action?.(),
            },
            {
              title: t('section.position.offset.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.OffsetXYZ,
              tooltipText: t('section.functions.createPoints.offsetInfo', { ns: 'skyviewDesign' }),
              onSelect: () => openCreatePointByOffsetDialog(props.tool).action?.(),
            },
          ],
          [
            {
              title: t('section.snapping.lines.length.cutFill.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.IntersectObjects,
              tooltipText: t('section.snapping.lines.length.cutFill.info', { ns: 'skyviewDesign' }),
              onSelect: () => onPlaceModeChanged(PlaceMode.IntersectObjects),
            },
          ],
        ]}
      />
    </CollapsableSection>
  );
};
